import { finishPlanningRecovery, finishRecoveryUpdates } from "./planning-recovery-gate"
import { recoveryDebug } from "./recovery-debug"
import { cloneDeep } from "lodash-es"
import DataHelper from "@/helper/data-helper"
import type { IBase } from "@/interfaces/api/base.interface"
import type { StreamPosition } from "./recovery"

// Collections persisted and restored during startup.
export const resources: Record<string, string> = {
  WorkPeriodTemplate: "administration/work-period-templates",
  InstructionFeedbackActivityTemplate: "planning/instruction-feedback-activity-templates",
  WorkspaceNote: "workspace/notes",
  WorkspaceAudienceGroup: "workspace/audience-groups",
  Order: "planning/orders",
  Machine: "planning/machines",
  Holiday: "planning/holidays",
  Product: "planning/products",
  Person: "core/persons",
  Project: "core/projects",
  ProjectGroup: "core/project-groups",
  KnownBusinessEntityContact: "core/known-business-entities-contacts",
  KnownBusinessEntity: "core/known-business-entities",
  Location: "core/locations",
  AvailabilityReason: "core/availability-reasons",
  PurchaseItem: "administration/purchase-items",
  SaleItem: "administration/sale-items",
  RentalIntakeItem: "administration/rental-intake-items",
  RentalDispenseItem: "administration/rental-dispense-items"
}

const modelsByResource = Object.fromEntries(
  Object.entries(resources).map(([model, resource]) => [resource, model])
)

type Snapshot = {
  scope: string
  savedAt: number
  position: StreamPosition
  startupGroups?: string[]
  records: Record<string, Record<string, IBase>>
}

// A missing connection or stalled recovery must still allow HTTP startup.
async function waitForStartup(promise: Promise<void>) {
  let timer: ReturnType<typeof setTimeout> | undefined
  try {
    await Promise.race([
      promise,
      new Promise<void>((resolve) => {
        timer = setTimeout(resolve, 5000)
      })
    ])
  } finally {
    clearTimeout(timer)
  }
}

function idle() {
  return new Promise<void>((resolve) =>
    "requestIdleCallback" in window
      ? requestIdleCallback(() => resolve(), { timeout: 5000 })
      : setTimeout(resolve, 2000)
  )
}

/** One atomic record: never save a cursor independently of the data it describes. */
export class NotificationSnapshot {
  private db?: IDBDatabase
  private scope = ""
  private startupGroups = new Set<string>()
  private shown = new Map<string, () => Promise<unknown>>()
  private restoredGroups = new Set<string>()
  private recovered = false
  private releaseRecovery!: () => void
  private recovery = new Promise<void>((resolve) => {
    this.releaseRecovery = resolve
  })
  wasHydrated = false
  private records: Snapshot["records"] = {}
  private position?: StreamPosition
  private timer?: ReturnType<typeof setTimeout>
  private ready = false
  private revision = 0
  // Counts live rows and clears. A response requested at an older count keeps its rows,
  // except rows the live feed touched since, and none after a clear.
  generation = 0
  private cleared = 0
  private stamps: Record<string, number> = {}
  private settled?: () => Promise<void>
  private releaseStartup!: () => void
  private startup = new Promise<void>((resolve) => {
    this.releaseStartup = resolve
  })

  async beforeRead(resource: string) {
    if (!modelsByResource[resource] && resource !== "planning/instructions") return
    // Establish the cursor before taking a baseline. An unavailable socket must not block HTTP forever.
    await waitForStartup(this.startup)
  }

  allowStartupRequests() {
    this.releaseStartup()
  }

  async open(scope: string) {
    this.scope = scope
    try {
      this.db = await new Promise<IDBDatabase>((resolve, reject) => {
        const request = indexedDB.open("peanuts-notification-snapshot-v1", 1)
        request.onupgradeneeded = () => request.result.createObjectStore("state")
        request.onsuccess = () => resolve(request.result)
        request.onerror = () => reject(request.error)
      })
      const saved = await new Promise<Snapshot | undefined>((resolve, reject) => {
        const request = this.db!.transaction("state").objectStore("state").get("snapshot")
        request.onsuccess = () => resolve(request.result)
        request.onerror = () => reject(request.error)
      })
      if (saved?.scope === scope && Date.now() - saved.savedAt < 7 * 86400000) {
        this.records = saved.records
        this.position = saved.position
        this.startupGroups = new Set(saved.startupGroups ?? [])
        this.restoredGroups = new Set(this.startupGroups)
      }
      return this.position
    } catch (error) {
      console.warn("[change-feed] Snapshot unavailable; using HTTP startup", error)
    }
  }

  private startupRestorer?: () => Promise<void>

  setStartupRestorer(restore: () => Promise<void>) {
    this.startupRestorer = restore
  }

  async restoreAtStartup() {
    if (!this.startupRestorer) throw new Error("Startup controller is not ready")
    await this.startupRestorer()
    this.wasHydrated = !!this.db && !!this.position
  }

  // Called after the final recovered publication has been queued. HTTP startup waits
  // here, while the notification fetches only wait for hydration (beforeRead).
  async finishRecovery(settled: () => Promise<void>) {
    const generation = this.generation
    try {
      await settled()
      this.recovered =
        this.wasHydrated && !!this.db && !!this.position && generation >= this.cleared
    } catch (error) {
      this.clear()
      console.warn("[change-feed] Recovery failed; using HTTP startup", error)
    } finally {
      recoveryDebug.finish(this.recovered)
      finishPlanningRecovery()
      finishRecoveryUpdates()
      this.releaseRecovery()
    }
  }

  async runStartupGroup(
    key: string,
    models: string[],
    load: () => Promise<unknown>,
    onRestored?: () => void
  ) {
    await this.beforeRead("planning/orders")
    if (
      this.wasHydrated &&
      this.restoredGroups.has(key) &&
      models.every((model) => this.records[model])
    ) {
      onRestored?.()
      this.shown.set(key, load) // Loaded over HTTP after all should the recovery fail.
      console.info("[change-feed] Using restored startup group", key)
      return
    }
    const generation = this.generation
    const result = await load()
    const failed = Array.isArray(result) && result.some((item) => item?.status === "rejected")
    if (
      !failed &&
      this.db &&
      this.ready &&
      generation >= this.cleared &&
      models.every((model) => this.records[model])
    ) {
      this.startupGroups.add(key)
      if (this.position && this.settled) this.checkpoint(this.position, this.settled)
    }
  }

  /** The cached rows of a model, as plain copies. */
  rows(model: string): IBase[] {
    return JSON.parse(JSON.stringify(Object.values(this.records[model] ?? {})))
  }

  async restoreCollections(apply: (collections: Record<string, IBase[]>) => Promise<void>) {
    const collections: Record<string, IBase[]> = {}
    for (const model of Object.keys(resources)) {
      if (this.records[model])
        collections[model] = JSON.parse(JSON.stringify(Object.values(this.records[model])))
    }
    await apply(collections)
    console.info(
      "[change-feed] Restored collections",
      Object.fromEntries(Object.entries(collections).map(([model, rows]) => [model, rows.length]))
    )
  }

  record(resource: string, rows: IBase[], generation = this.generation) {
    const model =
      modelsByResource[resource] ??
      (resource === "planning/instructions" ? "Instruction" : undefined)
    if (!this.db || !model || !Array.isArray(rows) || generation < this.cleared) return
    const records = (this.records[model] ??= {})
    // Store plain JSON, not mutable model instances or Moment objects.
    for (const row of rows) {
      if (!row.id || (this.stamps[`${model}:${row.id}`] ?? 0) > generation) continue
      records[row.id] = DataHelper.convertMomentToDateStrings(cloneDeep(row))
      if (model === "Instruction") {
        for (const order of Object.values(this.records.Order || {})) {
          const data = order as IBase & { instructions?: IBase[] }
          if (data.instructions) {
            data.instructions = data.instructions.filter((child) => child.id !== row.id)
            if ((row as IBase & { order?: string }).order === order.id) {
              data.instructions.push(records[row.id])
            }
          }
        }
      }
    }
    if (this.ready && this.position && this.settled) this.checkpoint(this.position, this.settled)
  }

  // Mirror live payloads into saved JSON without fetching or changing UI handlers.
  applyLiveMessage(message: any) {
    if (!this.db || !["model_create", "model_update", "model_delete"].includes(message.type)) return
    const { model } = message.details
    const data = message.details.isBulk
      ? DataHelper.denormalizeData(message.details.data)
      : message.details.data
    const rows = Array.isArray(data) ? data : [data]
    for (const row of rows) {
      if (!row?.id) continue
      const deleted = message.type === "model_delete"
      this.stamps[`${model}:${row.id}`] = ++this.generation
      if (deleted) this.remove(model, row.id)
      else if (resources[model] || model === "Instruction") {
        this.records[model] ??= {}
        this.records[model][row.id] = { ...this.records[model][row.id], ...cloneDeep(row) }
      }
      const patch = (value: any): any => {
        if (Array.isArray(value))
          return value.filter((child) => !deleted || child?.id !== row.id).map(patch)
        if (!value || typeof value !== "object") return value
        if (value.id === row.id) {
          if (deleted) return null
          Object.assign(value, cloneDeep(row))
        }
        for (const key of Object.keys(value)) value[key] = patch(value[key])
        return value
      }
      patch(this.records)
      if (model === "Instruction" && !deleted) {
        const parent = this.records.Order?.[row.order] as
          (IBase & { instructions?: IBase[] }) | undefined
        if (parent?.instructions && !parent.instructions.some((child) => child.id === row.id))
          parent.instructions.push(cloneDeep(row))
      }
    }
  }

  // Find cached roots containing a changed entity or one of its parent references.
  // Fetching these roots keeps nested payloads current without JSON patch rules.
  affectedCollections(ids: Set<string>): Record<string, string[]> {
    const contains = (value: unknown): boolean => {
      if (typeof value === "string") return ids.has(value)
      if (!value || typeof value !== "object") return false
      return Object.values(value).some(contains)
    }
    return Object.fromEntries(
      Object.entries(this.records)
        .filter(([model]) => !!resources[model])
        .map(([model, rows]) => [
          model,
          Object.values(rows)
            .filter(contains)
            .map((row) => row.id!)
        ])
    )
  }

  remove(model: string, id: string) {
    delete this.records[model]?.[id]
    if (model === "Order") {
      for (const [key, row] of Object.entries(this.records.Instruction || {})) {
        if ((row as IBase & { order?: string }).order === id) delete this.records.Instruction[key]
      }
    }
    // Order payloads also embed instructions. Don't restore a deleted child from its parent.
    if (model === "Instruction") {
      for (const order of Object.values(this.records.Order || {})) {
        const data = order as IBase & { instructions?: IBase[] }
        if (data.instructions) data.instructions = data.instructions.filter((row) => row.id !== id)
      }
    }
  }

  pause() {
    this.ready = false
    this.revision++
    clearTimeout(this.timer)
  }

  disable() {
    this.releaseStartup()
    this.releaseRecovery()
    this.clear()
    this.db?.close()
    this.db = undefined
  }

  clear() {
    this.pause()
    for (const load of this.shown.values()) void load()
    this.shown.clear()
    this.cleared = ++this.generation
    this.stamps = {}
    this.records = {}
    this.position = undefined
    this.recovered = false
    this.startupGroups.clear()
    this.restoredGroups.clear()
    if (this.db) this.db.transaction("state", "readwrite").objectStore("state").delete("snapshot")
  }

  /** Every response and live row postpones the save; a trickle of fetches after startup saves once, at its end. */
  static readonly QUIET_MS = 5000

  checkpoint(position: StreamPosition, settled: () => Promise<void>) {
    this.position = position
    this.settled = settled
    this.ready = true
    const revision = ++this.revision
    clearTimeout(this.timer)
    this.timer = setTimeout(async () => {
      await settled()
      await idle() // Serialising the snapshot is heavy; let the planning finish rendering first.
      if (!this.db || !this.ready || revision !== this.revision) return
      const snapshot: Snapshot = {
        startupGroups: [...this.startupGroups],
        scope: this.scope,
        savedAt: Date.now(),
        position,
        records: JSON.parse(JSON.stringify(this.records))
      }
      const transaction = this.db.transaction("state", "readwrite")
      transaction.objectStore("state").put(snapshot, "snapshot")
      transaction.oncomplete = () =>
        console.info(
          "[change-feed] Snapshot saved",
          JSON.stringify({
            since: position.since,
            orders: Object.keys(snapshot.records.Order || {}).length,
            instructions: Object.keys(snapshot.records.Instruction || {}).length
          })
        )
      transaction.onerror = () =>
        console.warn("[change-feed] Could not save snapshot", transaction.error)
    }, NotificationSnapshot.QUIET_MS)
  }
}

export const notificationSnapshot = new NotificationSnapshot()
